import { FC, useEffect, useMemo } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import {
  resolveStationPinAppearance,
  useMapLibreHoverPopup,
} from "@ryde/controls";
import {
  getIsValidStationLocation,
  stationsMapSelectedIdsAtom,
} from "@ryde/shared";

import { StationsMapMarker } from "./stations-map-marker";
import { useStationsMapSelectedStations } from "./use-stations-map-selected-stations";

export const StationsMapMarkers: FC = () => {
  const { openId, open, close, scheduleClose, cancelScheduledClose } =
    useMapLibreHoverPopup();

  const { selectedStations } = useStationsMapSelectedStations();
  const [mapSelectedIds, setMapSelectedIds] = useRecoilState(
    stationsMapSelectedIdsAtom,
  );
  const mapSelectedSet = useMemo(
    () => new Set(mapSelectedIds),
    [mapSelectedIds],
  );

  const gridSelectedIds = useMemo(
    () => new Set(selectedStations.map(station => station.id)),
    [selectedStations],
  );

  // Drop the orange map-selection for any station unchecked in the grid.
  useEffect(() => {
    setMapSelectedIds(prev => {
      const next = prev.filter(id => gridSelectedIds.has(id));

      return next.length === prev.length ? prev : next;
    });
  }, [gridSelectedIds, setMapSelectedIds]);

  // A popup only ever belongs to a map-selected station, so deselecting has to take it with it.
  useEffect(() => {
    if (openId !== null && !mapSelectedSet.has(openId)) {
      close(openId);
    }
  }, [openId, mapSelectedSet, close]);

  // Click toggles selection (orange highlight) — mirrors the passengers map.
  const handleStationClick = useEvent((stationId: number): void => {
    setMapSelectedIds(prev => {
      const next = new Set(prev);

      if (!next.delete(stationId)) {
        next.add(stationId);
      }

      return Array.from(next);
    });
  });

  const onHoverMarker = useEvent((stationId: number): void => {
    cancelScheduledClose();

    // Only a selected marker opens its popup on hover.
    if (mapSelectedSet.has(stationId)) {
      open(stationId);
    }
  });

  const onLeaveMarker = useEvent((stationId: number): void => {
    if (mapSelectedSet.has(stationId)) {
      scheduleClose(stationId);
    }
  });

  return (
    <>
      {selectedStations.map(station => {
        const location = station.metadata?.locationInfo;

        if (!getIsValidStationLocation(location)) {
          return null;
        }

        const pin = resolveStationPinAppearance({
          isFlagVariant: Boolean(station.metadata?.isOfficial),
          isSelected: mapSelectedSet.has(station.id),
        });

        return (
          <StationsMapMarker
            key={station.id}
            station={station}
            latitude={location.latitude}
            longitude={location.longitude}
            pinType={pin.type}
            pinColor={pin.color}
            isPopupOpen={openId === station.id}
            onClick={handleStationClick}
            onHover={onHoverMarker}
            onLeave={onLeaveMarker}
            onPopupEnter={open}
            onPopupLeave={scheduleClose}
            onPopupClose={close}
          />
        );
      })}
    </>
  );
};
